import { QRCodeSVG } from 'qrcode.react';
import {
    DEFAULT_PASS_BACKGROUND_COLOR,
    DEFAULT_PASS_TEXT_COLOR,
} from '../../../shared/constants/loyalPassBrand';

function absoluteUrl(raw) {
    const t = (raw || '').trim();
    if (!t) {
        return '';
    }
    return /^https?:\/\//i.test(t) ? t : `https://${t}`;
}

/** Google Wallet loyalty pass body only (no wallet chrome or footer). */
export default function GoogleWalletPassPreview({
    merchantName,
    programName,
    logoUrl,
    stripImageUrl,
    backgroundImageUrl,
    passBackgroundColor = DEFAULT_PASS_BACKGROUND_COLOR,
    passTextColor = DEFAULT_PASS_TEXT_COLOR,
    points = 0,
    visits = 0,
    barcodeSampleMessage = '00000000-0000-0000-0000-000000000000',
    barcodeAltText = 'customer@example.com',
}) {
    const logoSrc = absoluteUrl(logoUrl);
    const stripSrc = absoluteUrl(stripImageUrl);
    const bgSrc = absoluteUrl(backgroundImageUrl);
    const textStyle = { color: passTextColor };

    return (
        <div className="mx-auto w-full max-w-[320px] font-[system-ui,'Segoe_UI',Roboto,sans-serif]">
            <div
                className="relative overflow-hidden rounded-[20px] shadow-[0_4px_24px_rgba(0,0,0,0.18)] ring-1 ring-black/10"
                style={{ backgroundColor: passBackgroundColor }}
            >
                {bgSrc ? (
                    <div
                        className="pointer-events-none absolute inset-0 bg-cover bg-center opacity-[0.22]"
                        style={{ backgroundImage: `url(${bgSrc})` }}
                        aria-hidden
                    />
                ) : null}

                <div className="relative z-[1] px-4 pb-1 pt-4" style={textStyle}>
                    <div className="flex items-center gap-3">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.2)]">
                            {logoSrc ? (
                                <img src={logoSrc} alt="" className="h-full w-full object-contain p-1" />
                            ) : (
                                <span className="text-[10px] font-semibold text-neutral-500">Logo</span>
                            )}
                        </div>
                        <p className="min-w-0 flex-1 truncate text-[15px] font-normal leading-snug">
                            {merchantName || 'Your business'}
                        </p>
                    </div>
                </div>

                {stripSrc ? (
                    <div className="relative z-[1] mt-2 border-y border-white/10">
                        <img src={stripSrc} alt="" className="h-[76px] w-full object-cover" />
                    </div>
                ) : null}

                <div className="relative z-[1] px-4 pb-6 pt-3" style={textStyle}>
                    <h2 className="text-[1.65rem] font-bold leading-tight tracking-tight">
                        {programName || 'Program'}
                    </h2>

                    <div className="mt-5 flex w-full items-start justify-between gap-4">
                        <div className="min-w-0 shrink">
                            <p className="text-[0.8125rem] font-medium leading-none opacity-95">Points</p>
                            <p className="mt-1.5 text-[1.375rem] font-semibold tabular-nums leading-none">{points}</p>
                        </div>
                        <div className="min-w-0 shrink text-right">
                            <p className="text-[0.8125rem] font-medium leading-none opacity-95">Visits</p>
                            <p className="mt-1.5 text-[1.375rem] font-semibold tabular-nums leading-none">{visits}</p>
                        </div>
                    </div>

                    <div className="mt-6 flex flex-col items-center">
                        <div className="rounded-lg bg-white p-2.5 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.06)]">
                            <QRCodeSVG
                                value={barcodeSampleMessage}
                                size={152}
                                level="M"
                                fgColor="#111111"
                                bgColor="#ffffff"
                            />
                        </div>
                        <p
                            className="mt-2.5 max-w-[260px] truncate px-1 text-center text-[0.8125rem] font-medium leading-tight"
                            style={textStyle}
                        >
                            {barcodeAltText}
                        </p>
                        <p
                            className="mt-3 text-center text-[10px] font-medium leading-tight opacity-80"
                            style={textStyle}
                        >
                            Powered by LoyalPass
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
